const TRANSITLAND_API_KEY = process.env.REACT_APP_TRANSITLAND_API_KEY;
// TODO:  add multiple keys and do round robin of keys: 1000queries/month
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

export const getStopType = (routeType) => {
    switch (routeType) {
        case 0:
        case 900:
            return 'tram';
        case 1:
        case 2:
        case 100:
            return 'train';
        case 3:
        case 700:
        default:
            return 'bus';
    }
};

export const fetchNearbyTransitStops = async (lat, lng, radius = 3000) => {
    if (!lat || !lng) return [];

    try {
        if (!TRANSITLAND_API_KEY) {
            return await fetchOSMTransitStops(lat, lng, radius);
        }

        const [trainResult, busResult] = await Promise.allSettled([
            fetch(
                `https://transit.land/api/v2/rest/stops?lat=${lat}&lon=${lng}&radius=${radius}&limit=500&served_by_route_types=1,2&apikey=${TRANSITLAND_API_KEY}`
            ),
            fetch(
                `https://transit.land/api/v2/rest/stops?lat=${lat}&lon=${lng}&radius=${radius}&limit=500&served_by_route_types=3&apikey=${TRANSITLAND_API_KEY}`
            ),
        ]);

        const processResult = async (result) => {
            if (result.status === 'fulfilled' && result.value.ok) {
                const data = await result.value.json();
                return (
                    data.stops?.map((stop) => ({
                        id: stop?.onestop_id || stop?.id,
                        name: stop?.stop_name || stop.name || 'Transit Stop',
                        lat: stop.geometry.coordinates[1],
                        lng: stop.geometry.coordinates[0],
                        type: result === trainResult ? 'train' : 'bus',
                        provider: stop.operated_by_name || 'public',
                        distance: calculateDistance(
                            lat,
                            lng,
                            stop.geometry.coordinates[1],
                            stop.geometry.coordinates[0]
                        ),
                        routes: stop.routes ? stop.routes.map((r) => r.name || r.onestop_id) : [],
                    })) || []
                );
            }
            return [];
        };

        const [trainData, busData] = await Promise.all([processResult(trainResult), processResult(busResult)]);

        // Check if any API call was successful
        const hadSuccess =
            (trainResult.status === 'fulfilled' && trainResult.value.ok) ||
            (busResult.status === 'fulfilled' && busResult.value.ok);

        return hadSuccess ? [...trainData, ...busData] : await fetchOSMTransitStops(lat, lng, radius);
    } catch (err) {
        console.warn('TransitLand failed, falling back to OSM', err);
        return await fetchOSMTransitStops(lat, lng, radius);
    }
};

export const fetchOSMTransitStops = async (lat, lng, radius = 3000) => {
    const url = `https://overpass-api.de/api/interpreter?data=[out:json];(node["highway"="bus_stop"](around:${radius},${lat},${lng});node["railway"="tram_stop"](around:${radius},${lat},${lng});node["railway"="station"](around:${radius},${lat},${lng}););out;`;
    const res = await fetch(url);
    const data = await res.json();
    return data.elements.map((el) => ({
        id: el.id,
        name: el.tags?.name || `Stop ${el.id}`,
        lat: el.lat,
        lng: el.lon,
        type: el.tags?.highway === 'bus_stop' ? 'bus' : el.tags?.railway === 'tram_stop' ? 'tram' : 'train',
        provider: 'public',
        distance: calculateDistance(lat, lng, el.lat, el.lon),
    }));
};

export const fetchArrivalsForStop = async (stop) => {
    if (!stop || !TRANSITLAND_API_KEY) return [];
    try {
        // Get stop details
        const stopRes = await fetch(`https://transit.land/api/v2/rest/stops/${stop.id}?apikey=${TRANSITLAND_API_KEY}`);
        if (!stopRes.ok) throw new Error('Stop details fetch failed');
        const stopDetails = await stopRes.json();

        // Get departures
        const depRes = await fetch(
            `https://transit.land/api/v2/rest/stops/${stop.id}/departures?apikey=${TRANSITLAND_API_KEY}&limit=10`
        );
        if (!depRes.ok) throw new Error('Departures fetch failed');
        const departuresData = await depRes.json();

        return processArrivals(departuresData);
    } catch (err) {
        console.error('Error fetching arrivals:', err);
        return [];
    }
};

const processArrivals = (data) => {
    const departures = (data?.stops && data.stops[0]?.departures) || [];
    const now = new Date();

    return departures
        .slice(0, 10)
        .map((dep) => {
            const departureTimeStr = dep.departure?.estimated_local || dep.departure?.scheduled_local;
            const departureTime = new Date(departureTimeStr);
            const minutesUntil = Math.max(0, Math.floor((departureTime - now) / 60000));

            const route = dep.trip?.route || {};
            const agency = route?.agency || {};

            return {
                routeId: route?.route_id || route?.route_short_name || 'Unknown',
                routeLongName: route?.route_long_name || dep.trip?.trip_headsign || '',
                tripHeadSign: dep.trip?.trip_headsign || '',
                minutes: minutesUntil,
                arrivalTime: departureTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                isCanceled: dep.schedule_relationship === 'CANCELED',
                hasRealTime: !!dep.departure?.estimated_local,
                agencyName: agency?.agency_name || '-',
            };
        })
        .filter((p) => p.minutes <= 120 && !p.isCanceled)
        .sort((a, b) => a.minutes - b.minutes);
};
